import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Calendar, ChevronRight, ChevronDown, Pill, TestTube, NotebookPen,
  ShieldCheck, CheckCircle2, Edit3, Plus, Clock, CreditCard, Activity,
  HeartPulse, Hospital, AlertCircle, Stethoscope,
} from 'lucide-react';
import { AppointmentDetailDrawer } from './AppointmentDetailDrawer';
import axios from 'axios';

import { formatDateIST, formatDateTimeIST } from '../utils/date';
import { formatDoctorName } from '../utils/doctor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VISIT_TYPE_META = {
  OP: { label: 'OP', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: Stethoscope },
  IP: { label: 'IP', cls: 'bg-purple-100 text-purple-700 border-purple-200', icon: Hospital },
  ER: { label: 'ER', cls: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle },
  CR: { label: 'CR', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: HeartPulse },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function truncate(text, len = 120) {
  if (!text) return '';
  return text.length > len ? text.slice(0, len) + '...' : text;
}

// Group visits by Year-Month bucket. Returns [{key, label, items}], sorted
// most-recent first. Months with 0 visits are dropped.
function groupByMonth(visits) {
  const buckets = new Map();
  visits.forEach((v) => {
    if (!v.date) return;
    const d = new Date(v.date);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });
    if (!buckets.has(key)) buckets.set(key, { key, label, items: [] });
    buckets.get(key).items.push(v);
  });
  return Array.from(buckets.values())
    .sort((a, b) => b.key.localeCompare(a.key));
}

export function VisitHistory({ patientId, activeConsultationId, initialTab }) {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  // Visit type filter: all | OP | IP | ER | CR
  const [typeFilter, setTypeFilter] = useState('all');
  // Mode filter (only relevant when type=all or OP). Maps to backend `include`:
  //   'consulting' → completed bucket, 'appointments' → upcoming bucket
  const [modeFilter, setModeFilter] = useState('consulting');
  const [drawerApptId, setDrawerApptId] = useState(null);
  const [drawerApptIsCurrent, setDrawerApptIsCurrent] = useState(false);
  // Track which month groups are expanded. The most-recent month auto-opens.
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());
  const [showAuditLog, setShowAuditLog] = useState(false);

  const include = modeFilter === 'appointments' ? 'upcoming' : 'completed';
  const showModeFilter = typeFilter === 'all' || typeFilter === 'OP';
  const effectiveType = typeFilter === 'all' ? null : typeFilter;
  // For non-OP visit types (IP/ER/CR), "Appointments" bucket doesn't make sense
  // — those statuses don't apply. Force 'consulting' so the user can't land on
  // an empty bucket by accident.
  const effectiveInclude = showModeFilter ? include : 'completed';

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { include: effectiveInclude, limit: 300 };
        if (effectiveType) params.visit_type = effectiveType;
        const visitRes = await axios.get(
          `${API_URL}/api/appointments/patient/${patientId}/visits`,
          { params, withCredentials: true }
        ).catch(() => ({ data: [] }));
        if (cancelled) return;
        setVisits(visitRes.data || []);
      } catch (e) {
        if (!cancelled) console.error('Visit history error:', e);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [patientId, effectiveInclude, effectiveType]);

  // Audit log fetched independently — depends only on the active consultation
  // so filter pill clicks don't clobber it (testing_agent iter37 RCA).
  useEffect(() => {
    if (!activeConsultationId) {
      setAuditLog([]);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/api/consultations/${activeConsultationId}/audit-log`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setAuditLog(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setAuditLog([]); });
    return () => { cancelled = true; };
  }, [activeConsultationId]);

  const months = useMemo(() => groupByMonth(visits), [visits]);

  // Auto-expand the most recent month whenever the filter changes.
  useEffect(() => {
    if (months.length > 0) {
      setExpandedMonths(new Set([months[0].key]));
    } else {
      setExpandedMonths(new Set());
    }
  }, [months]);

  const toggleMonth = (key) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Summary stats for the header chip row.
  const totalVisits = visits.length;
  const completedCount = visits.filter((v) => v.consultation_completed).length;
  const opCount = visits.filter((v) => v.visit_type === 'OP').length;
  const ipCount = visits.filter((v) => v.visit_type === 'IP').length;
  const erCount = visits.filter((v) => v.visit_type === 'ER').length;
  const crCount = visits.filter((v) => v.visit_type === 'CR').length;

  return (
    <div className="h-full flex flex-col relative" data-testid="visit-history-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div>
          <h3 className="text-base font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>
            Visit History
          </h3>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {totalVisits} visit{totalVisits === 1 ? '' : 's'}
            {completedCount > 0 && ` · ${completedCount} with notes`}
          </p>
        </div>
        {activeConsultationId && (
          <button
            onClick={() => setShowAuditLog((v) => !v)}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
              showAuditLog
                ? 'bg-emerald-700 text-white border-emerald-700'
                : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'
            }`}
            data-testid="toggle-audit-log"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Audit Log{auditLog.length > 0 && ` (${auditLog.length})`}
          </button>
        )}
      </div>

      {/* Type filter pills */}
      <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="visit-type-filter">
        {[
          { key: 'all', label: 'All', count: totalVisits },
          { key: 'OP', label: 'OP', count: opCount },
          { key: 'IP', label: 'IP', count: ipCount },
          { key: 'ER', label: 'ER', count: erCount },
          { key: 'CR', label: 'CR', count: crCount },
        ].map((t) => {
          const active = typeFilter === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition ${
                active
                  ? 'bg-blue-700 text-white border-blue-700'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
              }`}
              data-testid={`type-filter-${t.key}`}
            >
              {t.label}
              {active && t.key !== 'all' && (
                <span className={`ml-1.5 text-[9px] ${active ? 'opacity-80' : 'text-gray-400'}`}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Mode (Consulting / Appointments) — only when type=All or OP */}
      {showModeFilter && (
        <div className="flex items-center gap-1 mb-3" data-testid="visit-mode-filter">
          {[
            { key: 'consulting', label: 'Consulting', testId: 'mode-consulting' },
            { key: 'appointments', label: 'Appointments', testId: 'mode-appointments' },
          ].map((m) => {
            const active = modeFilter === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setModeFilter(m.key)}
                className={`text-[11px] font-semibold px-3 py-1 rounded-md border transition ${
                  active
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={m.testId}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Body — month-grouped visit list, OR audit log if toggled */}
      <ScrollArea className="flex-1 min-h-0">
        {showAuditLog ? (
          activeConsultationId && auditLog.length > 0 ? (
            <div className="space-y-2 pb-3" data-testid="audit-list">
              {auditLog.map((entry, i) => <AuditEntryCard key={i} entry={entry} />)}
            </div>
          ) : (
            <EmptyState icon={ShieldCheck} text="No audit entries yet" subtext="Edits will appear here with before/after values." />
          )
        ) : loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400" data-testid="visit-history-loading">
            <Clock className="h-6 w-6 animate-pulse mr-2" />
            <span className="text-sm">Loading visits…</span>
          </div>
        ) : months.length === 0 ? (
          <EmptyState
            icon={Calendar}
            text={
              effectiveInclude === 'upcoming'
                ? 'No upcoming appointments'
                : effectiveType
                ? `No ${effectiveType} visits found`
                : 'No completed visits yet'
            }
          />
        ) : (
          <div className="space-y-3" data-testid="visit-month-list">
            {months.map((m) => {
              const expanded = expandedMonths.has(m.key);
              return (
                <div key={m.key} className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid={`month-${m.key}`}>
                  <button
                    onClick={() => toggleMonth(m.key)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-gray-50 to-white hover:bg-gray-100 transition"
                    data-testid={`month-toggle-${m.key}`}
                  >
                    <div className="flex items-center gap-2">
                      {expanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                      <span className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>{m.label}</span>
                      <Badge variant="outline" className="text-[10px] ml-1">{m.items.length}</Badge>
                    </div>
                    <MonthSparkline items={m.items} />
                  </button>
                  {expanded && (
                    <div className="divide-y divide-gray-100">
                      {m.items.map((visit, idx) => (
                        <VisitRow
                          key={visit.appointment_id || idx}
                          visit={visit}
                          index={idx}
                          isCurrentDoctor={visit.doctor_id === user?.external_doctor_id || visit.doctor_id === user?.doctor_id}
                          onClick={() => {
                            setDrawerApptId(visit.appointment_id);
                            setDrawerApptIsCurrent(visit.doctor_id === user?.external_doctor_id || visit.doctor_id === user?.doctor_id);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Detail drawer */}
      {drawerApptId && (
        <AppointmentDetailDrawer
          appointmentId={drawerApptId}
          isCurrentDoctor={drawerApptIsCurrent}
          onClose={() => setDrawerApptId(null)}
        />
      )}
    </div>
  );
}

// Single row inside an expanded month accordion. Compact (one-line on mobile)
// but content-rich: visit type chip, doctor, time, completed badge, status.
function VisitRow({ visit, index, isCurrentDoctor, onClick }) {
  const meta = VISIT_TYPE_META[visit.visit_type] || VISIT_TYPE_META.OP;
  const TypeIcon = meta.icon;
  const isCompleted = visit.consultation_completed || visit.status === 'completed' || visit.status === 'closed';
  const date = visit.date ? new Date(visit.date) : null;
  const dayLabel = date
    ? date.toLocaleDateString('en-IN', { day: '2-digit', weekday: 'short', timeZone: 'Asia/Kolkata' })
    : '';
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50/40 transition-colors text-left group"
      data-testid={`ext-visit-${index}`}
      data-appointment-id={visit.appointment_id || ''}
    >
      {/* Date column */}
      <div className="text-center flex-shrink-0 w-12">
        <p className="text-base font-bold text-gray-900 leading-none">
          {date ? String(date.getDate()).padStart(2, '0') : '—'}
        </p>
        <p className="text-[9px] text-gray-400 uppercase font-bold tracking-wider mt-0.5">
          {date ? date.toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'Asia/Kolkata' }) : ''}
        </p>
      </div>

      {/* Visit type icon */}
      <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border flex-shrink-0 ${meta.cls}`}>
        <TypeIcon className="h-4 w-4" />
      </span>

      {/* Main info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">
            {formatDoctorName(visit.doctor_name)}
          </span>
          {isCurrentDoctor && (
            <span className="text-[9px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0 rounded border border-blue-200">
              YOU
            </span>
          )}
          {visit.specialisation && (
            <span className="text-[10px] text-gray-500 truncate max-w-[140px]">· {visit.specialisation}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {visit.time && <span className="text-[10px] text-gray-500">{visit.time}</span>}
          <span className="text-[10px] text-gray-400">· {timeAgo(visit.date)}</span>
          {visit.payment_done && (
            <span className="text-[9px] text-emerald-700 bg-emerald-50 px-1 py-0 rounded">Paid</span>
          )}
          {visit.scheme && (
            <span className="text-[9px] text-violet-700 bg-violet-50 px-1 py-0 rounded">{visit.scheme}</span>
          )}
        </div>
      </div>

      {/* Status pills */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {visit.consultation_completed && (
          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px] gap-0.5" data-testid={`badge-notes-${index}`}>
            <CheckCircle2 className="h-2.5 w-2.5" /> Notes
          </Badge>
        )}
        <Badge
          className={`text-[9px] border-0 ${
            isCompleted ? 'bg-emerald-100 text-emerald-700' :
            visit.status === 'in-visit' ? 'bg-amber-100 text-amber-700' :
            visit.status === 'checked-in' ? 'bg-blue-100 text-blue-700' :
            visit.status === 'tentative' ? 'bg-gray-100 text-gray-600' :
            'bg-blue-100 text-blue-800'
          }`}
        >
          {(visit.status || '').replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || '—'}
        </Badge>
        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-600 transition" />
      </div>
    </button>
  );
}

// Tiny inline at-a-glance for each month — "3 OP · 1 IP" so the doctor knows
// what kind of visits a closed accordion holds without expanding.
function MonthSparkline({ items }) {
  const counts = items.reduce((acc, v) => {
    const t = v.visit_type || '?';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const chips = Object.entries(counts).filter(([, n]) => n > 0);
  if (chips.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5">
      {chips.map(([type, n]) => {
        const meta = VISIT_TYPE_META[type] || VISIT_TYPE_META.OP;
        return (
          <span
            key={type}
            className={`text-[9px] font-bold px-1.5 py-0 rounded ${meta.cls}`}
            title={`${n} ${type} visit${n === 1 ? '' : 's'}`}
          >
            {n} {type}
          </span>
        );
      })}
    </div>
  );
}

function EmptyState({ icon: Icon, text, subtext }) {
  return (
    <div className="text-center py-16 text-gray-400">
      <Icon className="h-10 w-10 mx-auto mb-3 opacity-40" />
      <p className="text-sm font-medium">{text}</p>
      {subtext && <p className="text-xs mt-1">{subtext}</p>}
    </div>
  );
}

const SECTION_LABEL = {
  chief_complaints: 'Chief Complaints',
  history_of_present_illness: 'History of Present Illness',
  past_history: 'Past History',
  tumor_board: 'Tumor Board',
  examination: 'Clinical Examination',
  general_info: 'General Info / Vitals',
  diagnosis: 'Diagnosis',
  prescription: 'Prescription',
  investigations: 'Investigations',
  advice: 'Advice',
  review_date: 'Review Date',
  admission_request: 'Admission Request',
};

function renderAuditValue(val) {
  if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
    return <span className="italic text-gray-400">(empty)</span>;
  }
  if (Array.isArray(val)) {
    return (
      <ul className="list-disc ml-4 space-y-0.5">
        {val.map((item, i) => (
          <li key={i} className="text-xs">
            {typeof item === 'string' ? stripHtml(item) : (item?.medicine_name || item?.name || JSON.stringify(item))}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof val === 'object') {
    return (
      <div className="font-mono text-[11px] bg-white/60 rounded p-1.5 max-h-28 overflow-auto">
        {JSON.stringify(val, null, 2)}
      </div>
    );
  }
  const text = typeof val === 'string' ? stripHtml(val) : String(val);
  return <span className="whitespace-pre-wrap break-words">{truncate(text, 240)}</span>;
}

function AuditEntryCard({ entry }) {
  const timeLabel = entry.timestamp ? formatDateTimeIST(entry.timestamp) : '—';
  const isEdit = entry.action === 'edit';
  const isPost = !!entry.post_completion;
  return (
    <div
      className={`rounded-lg border p-3 ${isPost ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}
      data-testid={`audit-entry-${entry.section}-${entry.timestamp}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="flex items-center gap-2">
          {isEdit ? <Edit3 className="h-4 w-4 text-amber-600" /> : <Plus className="h-4 w-4 text-emerald-600" />}
          <span className="text-sm font-semibold text-gray-800">{SECTION_LABEL[entry.section] || entry.section}</span>
          <Badge variant="outline" className="text-[10px]">{isEdit ? 'Edited' : 'Added'}</Badge>
          {isPost && <Badge className="bg-amber-600 text-white text-[10px]">Post-completion</Badge>}
        </div>
        <div className="text-[11px] text-gray-500 flex items-center gap-1">
          <Clock className="h-3 w-3" />{timeLabel}
        </div>
      </div>
      <div className="text-[11px] text-gray-500 mb-2">
        By <span className="font-medium text-gray-700">{entry.user_name || 'Doctor'}</span>
      </div>
      {isEdit && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <div className="rounded border border-red-200 bg-red-50/50 p-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-red-600 mb-1">Before</div>
            <div className="text-gray-700">{renderAuditValue(entry.old_value)}</div>
          </div>
          <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">After</div>
            <div className="text-gray-700">{renderAuditValue(entry.new_value)}</div>
          </div>
        </div>
      )}
      {!isEdit && (
        <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2 text-xs text-gray-700">
          {renderAuditValue(entry.new_value)}
        </div>
      )}
    </div>
  );
}
